import React from "react";
import { Type, Sticker, MessageSquare, Trash2 } from "lucide-react";
import { TextElement, StickerElement, CaptionElement } from "../../types";

interface ElementsListProps {
  texts: TextElement[];
  stickers: StickerElement[];
  captions: CaptionElement[];
  selectedEntity: string | null;
  onEntitySelect: (id: string) => void;
  onDeleteEntity: (id: string) => void;
  onDeselectEntity: () => void;
}

const ElementsList: React.FC<ElementsListProps> = ({
  texts,
  stickers,
  captions,
  selectedEntity,
  onEntitySelect,
  onDeleteEntity,
  onDeselectEntity,
}) => {
  const allElements = [
    ...texts.map((text) => ({ ...text, type: "text" as const })),
    ...stickers.map((sticker) => ({ ...sticker, type: "sticker" as const })),
    ...captions.map((caption) => ({ ...caption, type: "caption" as const })),
  ].sort((a, b) => {
    // Sort by creation order (based on ID timestamp)
    return a.id.localeCompare(b.id);
  });

  const getElementIcon = (type: string) => {
    switch (type) {
      case "text":
        return Type;
      case "sticker":
        return Sticker;
      case "caption":
        return MessageSquare;
      default:
        return Type;
    }
  };

  return (
    <div
      className="space-y-3 mb-6 max-h-64 sm:max-h-96 overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {allElements.length === 0 ? (
        <div className="text-center text-gray-500 py-6 sm:py-8" dir="rtl">
          אין אלמנטים עדיין
        </div>
      ) : (
        allElements.map((element) => {
          const Icon = getElementIcon(element.type);
          const isSelected = selectedEntity === element.id;

          return (
            <div
              key={element.id}
              onClick={(e) => {
                e.stopPropagation();
                if (isSelected) {
                  onDeselectEntity();
                } else {
                  onEntitySelect(element.id);
                }
              }}
              className={`p-2 sm:p-3 lg:p-4 rounded-lg border cursor-pointer transition-all duration-200 transform scale-90 hover:scale-100 ${
                isSelected
                  ? "border-pink-500 bg-gradient-to-r from-pink-50 to-yellow-50 shadow-md"
                  : "border-gray-200 hover:border-pink-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteEntity(element.id);
                  }}
                  className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1 rounded hover:bg-red-50 flex-shrink-0"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
                <div className="flex-1 min-w-0">
                  {element.type === "text" && (
                    <p
                      className="px-1 sm:px-2 text-xs sm:text-sm lg:text-base text-gray-600 truncate"
                      dir="rtl"
                    >
                      {(element as TextElement).text}
                    </p>
                  )}
                  {element.type === "sticker" && (
                    <p
                      className="px-1 sm:px-2 text-xs sm:text-sm lg:text-base text-gray-600 truncate"
                      dir="rtl"
                    >
                      {(element as StickerElement).name || "סטיקר"}
                    </p>
                  )}
                  {element.type === "caption" && (
                    <p
                      className="px-1 sm:px-2 text-xs sm:text-sm lg:text-base text-gray-600 truncate"
                      dir="rtl"
                    >
                      {(element as CaptionElement).text}
                    </p>
                  )}
                </div>
                <Icon className="h-6 w-6 sm:h-6 sm:w-6 flex-shrink-0" />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ElementsList;
